import React, { useEffect, useState } from "react";
import { Card, Paper, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import { pageChartCardSx, pageChartTitleSx } from "../../styles/pageModernStyles";
import { DASHBOARD_CHART_COLORS } from "./dashboardChartUtils";
import { useTranslation } from "react-i18next";
import { numberLocaleFromI18n } from "../../utils/i18nLocale";
import DashboardChartTooltip from "./DashboardChartTooltip";
import { dashboardFixedTooltipProps } from "../../utils/dashboardChartUtils";
import { calcMargenMes } from "../../utils/margenesUtils";

import "dayjs/locale/es";
import "dayjs/locale/en";
import "dayjs/locale/zh-cn";

const MargenClienteAcumulado = ({ margenRows = [], year }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [data, setData] = useState([]);
  const [clientes, setClientes] = useState([]);

  const { i18n, t } = useTranslation();

  useEffect(() => {
    let lang = i18n.language.toLowerCase();
    if (lang === "ch" || lang.startsWith("zh")) lang = "zh-cn";
    else if (lang.startsWith("en")) lang = "en";
    else if (lang.startsWith("es")) lang = "es";
    else lang = "en";

    dayjs.locale(lang);
  }, [i18n.language]);

  useEffect(() => {
    const rows = margenRows.filter(
      (row) => Number(row.YEAR) === year && row.CLIPRO,
    );

    const grouped = {};
    rows.forEach((row) => {
      const month = Number(row.MONTH);
      const cliente = row.CLIPRO;
      const margen = calcMargenMes(row);

      if (!grouped[month]) grouped[month] = { month };
      grouped[month][cliente] = (grouped[month][cliente] || 0) + margen;
    });

    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const clientesUnicos = [...new Set(rows.map((r) => r.CLIPRO))];

    const acumulados = {};
    const dataFinal = months.map((m) => {
      const row = {
        month: dayjs()
          .month(m - 1)
          .format("MMM"),
      };

      clientesUnicos.forEach((cliente) => {
        acumulados[cliente] =
          (acumulados[cliente] || 0) + (grouped[m]?.[cliente] || 0);
        row[cliente] = acumulados[cliente];
      });

      return row;
    });

    setData(dataFinal);
    setClientes(clientesUnicos);
  }, [margenRows, year, i18n.language]);

  const colors = DASHBOARD_CHART_COLORS;

  return (
    <Card component={Paper} sx={pageChartCardSx(theme, { tall: true })}>
      <Typography variant="h6" sx={pageChartTitleSx}>
        {t("DashboardVentas.AccumulatedMarginsbyCustomer")} ({year})
      </Typography>

      <ResponsiveContainer width="100%" height="85%">
        <LineChart
          data={data}
          margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
        >
            <XAxis
              dataKey="month"
              tick={{ fontSize: isMobile ? 10 : 12, fill: theme.palette.text.primary }}
            />
            <YAxis
              width={isMobile ? 52 : 90}
              tick={{ fontSize: isMobile ? 10 : 12, fill: theme.palette.text.primary }}
              tickFormatter={(value) =>
                value >= 1_000_000
                  ? `${(value / 1_000_000).toFixed(1)}M`
                  : value.toLocaleString(numberLocaleFromI18n(i18n.language))
              }
            />
            <Tooltip
              {...dashboardFixedTooltipProps}
              content={(props) => (
                <DashboardChartTooltip
                  {...props}
                  valueFormatter={(value) =>
                    value.toLocaleString(numberLocaleFromI18n(i18n.language), {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }
                />
              )}
            />

            {clientes.map((cliente, idx) => (
              <Line
                key={cliente}
                type="monotone"
                dataKey={cliente}
                stroke={colors[idx % colors.length]}
                strokeWidth={isMobile ? 1.5 : 2}
                dot={{ r: isMobile ? 2 : 3 }}
              />
            ))}
          </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default MargenClienteAcumulado;
